import posthog from 'posthog-js';

// ---------------------------------------------------------------------------
// PostHog client for error / observability events.
//
// Mixpanel (`./analytics.ts`) continues to handle product analytics. PostHog
// is layered on top *exclusively* for error capture so we get one unified
// observability stack across apps/api, apps/engine, and apps/web (SIG-61/62/63).
//
// When VITE_POSTHOG_KEY is unset (local dev without PostHog wired up, or
// preview deploys) every helper becomes a no-op so we never crash a render
// path on missing env config.
// ---------------------------------------------------------------------------

const POSTHOG_KEY = import.meta.env['VITE_POSTHOG_KEY'] as string | undefined;
const POSTHOG_HOST =
  (import.meta.env['VITE_POSTHOG_HOST'] as string | undefined) ??
  'https://app.posthog.com';

let initialized = false;

export function initPostHog(): void {
  if (initialized) return;
  if (!POSTHOG_KEY) {
    console.warn('[web] VITE_POSTHOG_KEY not set — PostHog capture is a no-op');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    // Auto-captures window.onerror + unhandledrejection with stack traces.
    capture_exceptions: true,
    persistence: 'localStorage',
    // Mixpanel already tracks pageviews; don't double-count.
    capture_pageview: false,
    // We never want PostHog session recording for now — error events only.
    disable_session_recording: true,
  });

  initialized = true;
}

/**
 * Capture an arbitrary error/observability event.
 *
 * Per SIG-63 sensitive-data rules: callers must NEVER pass request bodies,
 * response payloads, article content, feed names, passwords, or auth tokens.
 * Only safe-by-construction fields like route, endpoint, status code, error
 * message, stack trace.
 */
export function capturePostHogEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.capture(event, properties);
}

export function identifyPostHog(
  userId: string,
  traits?: Record<string, unknown>,
): void {
  if (!initialized) return;
  posthog.identify(userId, traits);
}

export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
}

export function getPostHogClient(): typeof posthog {
  return posthog;
}
